import { exigirDefinicionCoherente } from '../formularios/definicion.js';
/**
 * Microcheck semanal · batería P1–P4 del primer semestre.
 *
 * Es el check-in corto del protocolo (D+7, D+14, D+21, semanas 5–7, 9–10, 14):
 * cuatro preguntas y, cuando el plan lo indica, el árbol del NMS a continuación
 * como aplicación propia de la escala. **El NMS no se inlinea acá**: es un
 * instrumento con su `Questionnaire`, su puntaje y su evento, y hundirlo dentro
 * de otro formulario haría que la misma medición se registre distinto según
 * dónde se preguntó.
 *
 * **El dolor no puntúa y no pinta color.** Definición de Fundación del 29/07,
 * ratificada el 06/08: el dolor genera un **reporte** al equipo asistencial y una
 * recomendación al paciente y al cuidador. Las reglas "dolor ≥ 7 → alerta roja"
 * del protocolo están derogadas por escrito. Se sigue preguntando porque el dato
 * importa; lo que se eliminó es el aviso.
 *
 * P1, P3 y P4 se registran sin puntaje: son estado, no escala. El único
 * instrumento que puntúa dentro de un check-in es el NMS.
 *
 * La batería del segundo semestre vive más abajo, en este mismo archivo.
 */
export const FORMULARIO_MICROCHECK = exigirDefinicionCoherente({
    id: 'microcheck-semanal',
    clase: 'microcheck',
    titulo: '¿Cómo estás hoy?',
    version: '1.0.0',
    estado: 'active',
    preguntas: [
        {
            id: 'tratamiento',
            texto: '¿Seguís con el tratamiento activo?',
            campo: { tipo: 'binario', etiquetaSi: 'Sí', etiquetaNo: 'No', puntosSi: 0, puntosNo: 0 },
            presentacion: { modo: 'lista' },
            opcional: true,
        },
        {
            id: 'dolor',
            texto: '¿Cuánto dolor tuviste hoy?',
            campo: {
                tipo: 'puntaje',
                minimo: 0,
                maximo: 10,
                // Las dos anclas canónicas del NRS 0-10 (LOINC 72514-3, "Pain severity
                // 0-10 verbal numeric rating"). Antes decían "Nada" y "Mucho", que era
                // redacción nuestra: usar las del instrumento no es redactarlo, es
                // administrarlo entero. Los valores intermedios quedan sólo numéricos —
                // el NRS no nombra sus puntos medios y ponerles palabras sería inventar
                // bandas que el instrumento no tiene.
                etiquetaMinimo: 'Sin dolor',
                etiquetaMaximo: 'El peor dolor imaginable',
                // Reporte, no escala: no suma a ningún puntaje y no enciende el semáforo.
                puntua: false,
            },
            presentacion: { modo: 'emoji' },
            opcional: true,
        },
        {
            id: 'ejercicios',
            texto: '¿Hiciste los ejercicios esta semana?',
            campo: {
                tipo: 'opcion',
                opciones: [
                    { codigo: 'todos', etiqueta: 'Todos' },
                    { codigo: 'a-medias', etiqueta: 'A medias' },
                    { codigo: 'no-pude', etiqueta: 'No pude' },
                ],
            },
            presentacion: { modo: 'lista' },
            opcional: true,
        },
        {
            id: 'movilidad',
            texto: 'Comparado con la semana pasada, ¿cómo te movés?',
            campo: {
                tipo: 'opcion',
                opciones: [
                    { codigo: 'mejor', etiqueta: 'Mejor' },
                    { codigo: 'igual', etiqueta: 'Igual' },
                    { codigo: 'peor', etiqueta: 'Peor' },
                ],
            },
            presentacion: { modo: 'lista' },
            opcional: true,
        },
    ],
});
/** La pregunta de dolor. La nombra el servicio que emite `pain.reported`. */
export const PREGUNTA_DE_DOLOR = 'dolor';
// ─────────────────────────────────────────────────────────────────────────────
// Segundo semestre · consolidación
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Batería mensual del segundo semestre · **cierra P-47**.
 *
 * El protocolo cambia el foco a partir del mes 7: de recuperación funcional a
 * **calidad de vida, participación social y autonomía en la vida cotidiana del
 * adulto mayor**. Las cuatro primeras preguntas siguen ahí pero con otra
 * redacción —el dolor pasa de "hoy" a "promedio esta semana", la movilidad deja
 * de compararse con la semana anterior y se pregunta contra el antes—, y se suman
 * tres dimensiones nuevas.
 *
 * Igual que en el primer semestre, **el NMS no está adentro**: el protocolo lo
 * lista como P5 y se administra como aplicación propia de la escala.
 */
const TRATAMIENTO = {
    id: 'tratamiento',
    texto: '¿Seguís con el tratamiento activo?',
    campo: { tipo: 'binario', etiquetaSi: 'Sí', etiquetaNo: 'No', puntosSi: 0, puntosNo: 0 },
    presentacion: { modo: 'lista' },
    opcional: true,
};
const DOLOR_DEL_MES = {
    id: 'dolor',
    texto: '¿Cuánto dolor tuviste en promedio esta semana?',
    campo: {
        tipo: 'puntaje',
        minimo: 0,
        maximo: 10,
        // Las mismas dos anclas del NRS que en el primer semestre: lo que cambia
        // entre las dos baterías es el período que se pregunta, no el instrumento.
        etiquetaMinimo: 'Sin dolor',
        etiquetaMaximo: 'El peor dolor imaginable',
        // Reporte, no escala: igual que en el primer semestre, el dolor no puntúa y
        // no enciende el semáforo.
        puntua: false,
    },
    presentacion: { modo: 'emoji' },
    opcional: true,
};
const EJERCICIOS = {
    id: 'ejercicios',
    texto: '¿Realizás los ejercicios indicados?',
    campo: {
        tipo: 'opcion',
        opciones: [
            { codigo: 'todos', etiqueta: 'Todos' },
            { codigo: 'a-medias', etiqueta: 'A medias' },
            { codigo: 'no-pude', etiqueta: 'No pude' },
        ],
    },
    presentacion: { modo: 'lista' },
    opcional: true,
};
const MOVILIDAD_GENERAL = {
    id: 'movilidad',
    texto: '¿Podés hacer las actividades del día a día que hacías antes?',
    campo: {
        tipo: 'opcion',
        opciones: [
            { codigo: 'todas', etiqueta: 'Sí, todas' },
            { codigo: 'la-mayoria', etiqueta: 'La mayoría' },
            { codigo: 'pocas', etiqueta: 'Pocas' },
            { codigo: 'casi-ninguna', etiqueta: 'Casi ninguna' },
        ],
    },
    presentacion: { modo: 'lista' },
    opcional: true,
};
const CALIDAD_DE_VIDA = {
    id: 'calidad-de-vida',
    texto: '¿Cómo calificarías tu calidad de vida este mes?',
    campo: {
        tipo: 'opcion',
        opciones: [
            { codigo: 'muy-buena', etiqueta: 'Muy buena' },
            { codigo: 'buena', etiqueta: 'Buena' },
            { codigo: 'regular', etiqueta: 'Regular' },
            { codigo: 'mala', etiqueta: 'Mala' },
        ],
    },
    presentacion: { modo: 'lista' },
    opcional: true,
};
const PARTICIPACION_SOCIAL = {
    id: 'participacion',
    texto: '¿Pudiste retomar actividades sociales o recreativas?',
    ayuda: 'Por ejemplo: visitar familia, salir con amigos, ir a misa, hacer mandados.',
    campo: {
        tipo: 'opcion',
        opciones: [
            { codigo: 'si', etiqueta: 'Sí' },
            { codigo: 'a-medias', etiqueta: 'A medias' },
            { codigo: 'todavia-no', etiqueta: 'Todavía no' },
        ],
    },
    presentacion: { modo: 'lista' },
    opcional: true,
};
const AUTONOMIA = {
    id: 'autonomia',
    texto: '¿Podés desplazarte solo o sola fuera de tu casa sin depender de alguien?',
    campo: {
        tipo: 'opcion',
        opciones: [
            { codigo: 'solo', etiqueta: 'Sí, solo o sola' },
            { codigo: 'me-llevan', etiqueta: 'Necesito que me lleven' },
            { codigo: 'no-salgo', etiqueta: 'No salgo' },
        ],
    },
    presentacion: { modo: 'lista' },
    opcional: true,
};
const MARCHA_SOSTENIDA = {
    id: 'marcha',
    texto: '¿Podés caminar más de 20 minutos seguidos sin descansar?',
    campo: {
        tipo: 'opcion',
        opciones: [
            { codigo: 'si', etiqueta: 'Sí' },
            { codigo: 'a-veces', etiqueta: 'A veces' },
            { codigo: 'no', etiqueta: 'No' },
        ],
    },
    presentacion: { modo: 'lista' },
    opcional: true,
};
const ANIMO = {
    id: 'animo',
    texto: '¿Cómo está tu estado de ánimo este mes?',
    campo: {
        tipo: 'opcion',
        opciones: [
            { codigo: 'muy-bien', etiqueta: 'Muy bien' },
            { codigo: 'bien', etiqueta: 'Bien' },
            { codigo: 'regular', etiqueta: 'Regular' },
            { codigo: 'bajo', etiqueta: 'Bajo' },
        ],
    },
    presentacion: { modo: 'lista' },
    opcional: true,
};
/**
 * La batería que el protocolo declara compartida por M7, M9 y M11.
 *
 * P1 a P4 con la redacción del segundo semestre, más calidad de vida (P6),
 * participación social (P7) y autonomía en desplazamientos (P8).
 */
export const FORMULARIO_MICROCHECK_MENSUAL = exigirDefinicionCoherente({
    id: 'microcheck-mensual',
    clase: 'microcheck',
    titulo: '¿Cómo venís este mes?',
    version: '1.0.0',
    estado: 'active',
    preguntas: [
        TRATAMIENTO,
        DOLOR_DEL_MES,
        EJERCICIOS,
        MOVILIDAD_GENERAL,
        CALIDAD_DE_VIDA,
        PARTICIPACION_SOCIAL,
        AUTONOMIA,
    ],
});
/**
 * La ampliada del mes 9, que suma marcha sostenida (P9) y estado anímico (P10).
 *
 * **El ánimo es un dato, no un disparador.** El protocolo decía que "Bajo" dos
 * meses seguidos sugería derivación al equipo; esa regla quedó derogada con el
 * resto de las acciones automáticas (definición de Fundación 29/07, ratificada el
 * 06/08). Se sigue preguntando porque el dato importa en un adulto mayor
 * post-trauma; lo que se eliminó es que la app actúe por él.
 */
export const FORMULARIO_MICROCHECK_MENSUAL_AMPLIADO = exigirDefinicionCoherente({
    id: 'microcheck-mensual-ampliado',
    clase: 'microcheck',
    titulo: '¿Cómo venís este mes?',
    version: '1.0.0',
    estado: 'active',
    preguntas: [
        TRATAMIENTO,
        DOLOR_DEL_MES,
        EJERCICIOS,
        MOVILIDAD_GENERAL,
        CALIDAD_DE_VIDA,
        PARTICIPACION_SOCIAL,
        AUTONOMIA,
        MARCHA_SOSTENIDA,
        ANIMO,
    ],
});
//# sourceMappingURL=microcheck.js.map